import '.cd{display:inline-flex;}.ssr-plugin'
import '.t{align-items:center;}.ssr-plugin'
import '.e7{gap:0.375rem;}.ssr-plugin'
import '.hs{padding:0.375rem 0.625rem;}.ssr-plugin'
import '.f8{border-radius:0.5rem;}.ssr-plugin'
import '.ht{border:1px solid var(--gray-200);}.ssr-plugin'
import '.v{background-color:var(--palette-white);}.ssr-plugin'
import '.hu{max-width:12rem;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.aq{justify-content:center;}.ssr-plugin'
import '.at{flex-shrink:0;}.ssr-plugin'
import '.g3{background:none;}.ssr-plugin'
import '.c4{border:none;}.ssr-plugin'
import '.l{padding:0px;}.ssr-plugin'
import '.a1{cursor:pointer;}.ssr-plugin'
import '.bb{color:var(--gray-300);}.ssr-plugin'
import '.fa{width:1rem;}.ssr-plugin'
import '.fb{height:1rem;}.ssr-plugin'
import { MAX_POST_MESSAGE_LENGTH } from '@app/components/blocks/discussions/constants'
import { Text } from '@app/components/core/typography/Text'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { FileSelector } from '@app/components/core/input/text/FileSelector'
import { TextArea } from '@app/components/core/input/text/TextArea'
import { forwardRef, useCallback, useState, KeyboardEvent } from 'react'
import { useLanguage } from '@i18n/useLanguage'
import { i18n } from '@i18n/index'
import { styled } from 'inlines'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFile,
  faFilePdf,
  faImage,
  faPaperPlaneTop,
} from '@fortawesome/pro-solid-svg-icons'
import { faXmark } from '@fortawesome/pro-regular-svg-icons'

const MAX_FILES = 5
const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
]

export type ReplyTextBoxProps = {
  onSumbit: (text: string, files: File[]) => Promise<boolean>
  onBlur?: () => void
}

export const ReplyTextBox = forwardRef<HTMLTextAreaElement, ReplyTextBoxProps>(
  ({ onSumbit, onBlur }, ref) => {
    const [language] = useLanguage()
    const translations = i18n.languages[language].app.post
    const [text, setText] = useState('')
    const [postMedia, setPostMedia] = useState<File[]>([])
    const isAtMaxLength = text.length >= MAX_POST_MESSAGE_LENGTH

    const clearForm = useCallback(() => {
      setText('')
      setPostMedia([])
    }, [])

    const onBlurCallback = useCallback(() => {
      if (text.trim() === '' && postMedia.length === 0) {
        onBlur?.()
      }
    }, [text, postMedia.length, onBlur])

    const onSubmitCallback = useCallback(() => {
      if (text.trim() === '') {
        return
      }
      void onSumbit(text.trim(), postMedia).then((success) => {
        if (success) {
          clearForm()
        }
      })
    }, [text, postMedia, onSumbit, clearForm])

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault()
          onSubmitCallback()
        }
      },
      [onSubmitCallback],
    )

    const addFiles = useCallback((newFiles: File[]) => {
      setPostMedia((prev) => {
        const combined = [...prev, ...newFiles]
        const seen = new Set<string>()
        const unique = combined.filter((file) => {
          const key = `${file.name}-${file.size}`
          if (seen.has(key)) {
            return false
          }
          seen.add(key)
          return true
        })
        return unique.slice(0, MAX_FILES)
      })
    }, [])

    const removeFile = useCallback((index: number) => {
      setPostMedia((prev) => prev.filter((_, i) => i !== index))
    }, [])

    return (
      <Column gap="0.5rem" alignItems="stretch">
        <Row
          gap="0.5rem"
          style={{
            alignItems: 'flex-start',
            padding: '0.5rem 1rem',
            borderRadius: '0.75rem',
            background: 'var(--palette-white)',
            border: '1px solid var(--palette-blue-300)',
            '&:hover': {
              border: '1px solid var(--palette-blue-500)',
            },
            '&:focus-within': {
              border: '1px solid var(--brand-secondary)',
            },
          }}
        >
          <TextArea
            ref={ref}
            placeholder={translations.typeYourReply}
            value={text}
            onChange={(e) =>
              setText(e.target.value.slice(0, MAX_POST_MESSAGE_LENGTH))
            }
            onBlur={onBlurCallback}
            onKeyDown={handleKeyDown}
            maxLength={MAX_POST_MESSAGE_LENGTH}
            style={{ flexGrow: 1, minWidth: 0 }}
          />
          <FileSelector
            filesSelected={postMedia.length}
            maxFiles={MAX_FILES}
            allowedFileTypes={ALLOWED_FILE_TYPES}
            onFileSelectionUpdated={addFiles}
            iconStyle={{
              color: 'var(--palette-blue-200)',
              width: '1.125rem',
              height: '1.125rem',
              cursor: 'pointer',
              marginTop: '0.3125rem',
            }}
          />
          <FontAwesomeIcon
            icon={faPaperPlaneTop}
            onClick={text.trim() === '' ? undefined : onSubmitCallback}
            style={{
              height: '1.125rem',
              width: '1.125rem',
              color:
                text.trim() === ''
                  ? 'var(--gray-200)'
                  : 'var(--palette-blue-200)',
              cursor: text.trim() === '' ? 'default' : 'pointer',
              transition: 'color 0.15s',
              marginTop: '0.3125rem',
              flexShrink: 0,
            }}
          />
        </Row>
        {isAtMaxLength && (
          <Text.FormError>{translations.maxCharactersReached}</Text.FormError>
        )}
        {postMedia.length > 0 && (
          <Row gap="0.5rem" style={{ flexWrap: 'wrap' }}>
            {postMedia.map((file, index) => {
              const isPdf = file.type === 'application/pdf'
              const isImg = file.type.startsWith('image/')
              const icon = isPdf ? faFilePdf : isImg ? faImage : faFile
              return (
                <div className="cd t e7 hs f8 ht v hu"
                  key={`${file.name}-${file.lastModified}`}
                >
                  <FontAwesomeIcon
                    icon={icon}
                    style={{
                      width: '0.875rem',
                      height: '0.875rem',
                      color: isPdf
                        ? 'var(--palette-red-400)'
                        : 'var(--brand-secondary)',
                      flexShrink: 0,
                    }}
                  />
                  <Text.Small
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: 'var(--brand-secondary)',
                    }}
                  >
                    {file.name}
                  </Text.Small>
                  <button className="a t aq at g3 c4 l a1 bb fa fb"
                    type="button"
                    onClick={() => removeFile(index)}
                  >
                    <FontAwesomeIcon
                      icon={faXmark}
                      style={{ width: '0.75rem', height: '0.75rem' }}
                    />
                  </button>
                </div>
              )
            })}
          </Row>
        )}
      </Column>
    )
  },
)

ReplyTextBox.displayName = 'ReplyTextBox'
